import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";

/**
 * Global catch-all for any path that doesn't match a registered route.
 *
 * Without this file, an unmatched URL (typo'd path, stale external backlink,
 * old competitor-migration link, bot-guessed path, etc.) never throws
 * notFound() because no route loader runs at all — the router falls through
 * to its default "no match" rendering, which the SSR handler serves with a
 * 200 status. Google Search Console then classifies every one of these as
 * a real indexable 200 page, polluting the coverage report and spending
 * crawl budget on pages that were never meant to exist.
 *
 * Throwing notFound() here gives every truly-unmatched path a real 404
 * status (server.ts's addHtmlCacheHeaders already special-cases status 404
 * with a short, bot-friendly Cache-Control) and a real "page not found" UI
 * instead of a blank or default error screen.
 */
export const Route = createFileRoute("/$")({
  loader: () => {
    throw notFound();
  },
  head: () => ({
    meta: [
      { title: "Page Not Found | Heallexa" },
      {
        name: "description",
        content: "The page you're looking for doesn't exist or may have moved.",
      },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  notFoundComponent: NotFoundPage,
  component: NotFoundPage,
});

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 pt-32 md:pt-24 pb-20">
        <div className="text-center max-w-md">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">404</p>
          <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground mb-3">
            Page not found
          </h1>
          <p className="text-[14px] text-muted-foreground mb-8">
            The page you're looking for doesn't exist or may have moved. Try searching
            for a provider or heading back home.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
            >
              Go home
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors rounded-md"
            >
              Search providers
            </Link>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
